import { useState, useRef, useEffect } from "react";
import { Link, useRoute } from "wouter";
import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";
import { Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import { useStoreQuery, queries, mutations } from "@/lib/store";
import type { ChatMessage } from "@/lib/store";
import { useQuery as useConvexQuery, useMutation as useConvexMutation, useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";

type LydiaHistory = Array<{ role: "user" | "assistant"; content: string }>;

type SisterMeta = {
  name: string;
  initials: string;
  ringColor: string;
  tier: string;
  lifestage: string;
  isBot?: boolean;
  badge?: string;
  badgeColor?: string;
};

const SISTERS: Record<string, SisterMeta> = {
  lydia:   { name: "Sister Lydia",    initials: "SL", ringColor: "rgba(127,119,221,0.65)", tier: "Companion",    lifestage: "Form & nourishment", isBot: true, badge: "✦", badgeColor: "var(--mnf-purple)" },
  bethany: { name: "Bethany Cole",    initials: "BC", ringColor: "rgba(192,120,74,0.55)",  tier: "Seedling",     lifestage: "Postpartum" },
  rebecca: { name: "Rebecca Hayes",   initials: "RH", ringColor: "rgba(192,120,74,0.55)",  tier: "Flourishing",  lifestage: "Cycling" },
  joanna:  { name: "Joanna Pierce",   initials: "JP", ringColor: "rgba(168,212,184,0.55)", tier: "Rooted",       lifestage: "Perimenopause" },
  mara:    { name: "Mara Whitfield",  initials: "MW", ringColor: "rgba(160,120,32,0.55)",  tier: "Flourishing",  lifestage: "Cycling" },
  ruth:    { name: "Ruth Adler",      initials: "RA", ringColor: "rgba(168,212,184,0.55)", tier: "Rooted",       lifestage: "Cycling" },
  eden:    { name: "Eden Marsh",      initials: "EM", ringColor: "rgba(127,119,221,0.55)", tier: "Seedling",     lifestage: "Resting" },
};

function formatTime(sentAt: number): string {
  const d = new Date(sentAt);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const hh = d.getHours();
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh >= 12 ? "pm" : "am";
  const h12 = ((hh + 11) % 12) + 1;
  if (sameDay) return `Today · ${h12}:${mm} ${ampm}`;
  if (isYesterday) return `Yesterday · ${h12}:${mm} ${ampm}`;
  return `${d.toLocaleDateString("en-US", { weekday: "short" })} · ${h12}:${mm} ${ampm}`;
}

export default function Chat() {
  const [, params] = useRoute("/chat/:slug");
  const slug = (params?.slug || "lydia").toLowerCase();
  const sister = SISTERS[slug] || SISTERS.lydia;
  const isLydia = slug === "lydia";

  // Lydia uses real Convex persistence; all other threads stay in-memory.
  const convexMessages = useConvexQuery(
    api.chat.chatThread,
    isLydia ? { threadId: "lydia" } : "skip",
  );
  const sendConvexMessage = useConvexMutation(api.chat.sendChatMessage);
  const callLydiaAI = useAction(api.lydiaAI.chat);

  const inMemoryMessages = useStoreQuery(() => !isLydia ? queries.chatThread(slug) : []) as ChatMessage[];
  const messages: ChatMessage[] = isLydia ? (convexMessages ?? []) as ChatMessage[] : inMemoryMessages;

  const [draft, setDraft] = useState("");
  const [recording, setRecording] = useState(false);
  const [lydiaTyping, setLydiaTyping] = useState(false);
  // Transient, never persisted: a failed reply is UI state, not a stored message.
  const [lydiaError, setLydiaError] = useState<string | null>(null);
  const [retryPayload, setRetryPayload] = useState<{ message: string; history: LydiaHistory } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, lydiaTyping, lydiaError]);

  // Asks Lydia. Never throws, never writes a fabricated reply into the thread.
  async function askLydia(message: string, history: LydiaHistory): Promise<void> {
    setLydiaError(null);
    setLydiaTyping(true);
    try {
      await callLydiaAI({ threadId: "lydia", message, history });
      setRetryPayload(null);
    } catch (err) {
      console.error("[Chat] Sister Lydia reply failed", err);
      Sentry.captureException(err, { tags: { subsystem: "chat" } });
      setRetryPayload({ message, history });
      setLydiaError("Sister Lydia couldn't answer just now. Your words are saved — tap to ask her again.");
      toast({
        variant: "destructive",
        title: "Sister Lydia couldn't answer",
        description: "Your message is saved, but her reply didn't come through. Check your connection and tap Ask again.",
      });
    } finally {
      setLydiaTyping(false);
    }
  }

  async function runSend(): Promise<void> {
    const body = draft.trim();
    if (!body) return;
    setDraft("");

    if (isLydia) {
      const history: LydiaHistory = messages.slice(-6).map((m) => ({
        role: (m.fromCoach ? "assistant" : "user") as "user" | "assistant",
        content: m.body,
      }));
      try {
        await sendConvexMessage({ threadId: "lydia", type: "text", body });
      } catch (err) {
        console.error("[Chat] saving your message failed", err);
        Sentry.captureException(err, { tags: { subsystem: "chat" } });
        setDraft(body); // put her words back in the box — nothing was saved
        toast({
          variant: "destructive",
          title: "Your message didn't send",
          description: "We couldn't save what you wrote. Your words are still in the box — check your connection and send again.",
        });
        return;
      }
      await askLydia(body, history);
    } else {
      mutations.sendChatMessage({ threadId: slug, fromCoach: false, type: "text", body });
    }
  }

  // Backstop so no rejection ever leaks out of an onClick / onKeyDown handler.
  function guard(run: () => Promise<void>): void {
    void run().catch((err: unknown) => {
      console.error("[Chat] message handler failed", err);
      Sentry.captureException(err, { tags: { subsystem: "chat" } });
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "That didn't go through. Give it another try in a moment.",
      });
    });
  }

  function send(): void {
    guard(runSend);
  }

  function retryLydia(): void {
    const payload = retryPayload;
    if (!payload) return;
    guard(() => askLydia(payload.message, payload.history));
  }

  function sendVoice() {
    setRecording(false);
    mutations.sendChatMessage({
      threadId: slug,
      fromCoach: false,
      type: "voice",
      body: "Voice note",
      durationSec: 28,
    });
  }

  return (
    <div className="flex flex-col h-full" style={{ background: "var(--mnf-cr)" }}>
      {/* Header */}
      <div
        className="px-4 pt-2 pb-3 flex items-center gap-3 z-10"
        style={{
          background: "linear-gradient(165deg, #1E4A35 0%, #163828 100%)",
          color: "#F5F0E8",
          borderRadius: "0 0 24px 24px",
        }}
      >
        <Link href="/messages" className="no-underline" data-testid="button-back">
          <button
            className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(245,240,232,0.12)", color: "#F5F0E8" }}
          >
            <Icon name="arrow_back" size={18} />
          </button>
        </Link>
        <div className="relative">
          <Avatar initials={sister.initials} size={42} ringColor={sister.ringColor} />
          {sister.badge && (
            <span
              className="absolute flex items-center justify-center"
              style={{
                bottom: -2, right: -2,
                width: 18, height: 18, borderRadius: 999,
                background: sister.badgeColor || "var(--mnf-purple)",
                color: "#F5F0E8",
                fontSize: 10, fontWeight: 700,
                border: "2px solid #1E4A35",
              }}
              aria-hidden
            >
              {sister.badge}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="mnf-serif italic" style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.1 }}>{sister.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#A8D4B8" }} />
            <span className="mnf-body" style={{ fontSize: 11, color: "rgba(245,240,232,0.7)" }}>
              {sister.isBot ? sister.lifestage : `${sister.tier} · ${sister.lifestage}`}
            </span>
          </div>
        </div>
        <button
          className="flex items-center justify-center"
          style={{ width: 36, height: 36, borderRadius: 12, background: "rgba(245,240,232,0.12)", color: "#F5F0E8" }}
          data-testid="button-sister-profile"
        >
          <Icon name="person" size={18} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 mnf-scroll">
        {sister.isBot && (
          <div className="flex justify-center mb-2">
            <div
              className="mnf-body text-center"
              style={{
                fontSize: 11,
                color: "var(--mnf-purple)",
                padding: "6px 14px",
                background: "rgba(127,119,221,0.10)",
                borderRadius: 999,
                border: "1px solid rgba(127,119,221,0.20)",
                maxWidth: 280,
                lineHeight: 1.4,
              }}
            >
              Sister Lydia is a faith-rooted companion for form and nourishment questions.
            </div>
          </div>
        )}
        {messages.length === 0 && (
          <div className="flex justify-center" style={{ marginTop: 32 }}>
            <p className="mnf-body italic text-center" style={{ fontSize: 13, color: "var(--mnf-tg)", maxWidth: 240, lineHeight: 1.5 }}>
              {sister.isBot
                ? "Ask about squat depth, protein per meal, postpartum return, or anything in between."
                : "Send your first word."}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <Bubble
            key={m.id}
            msg={m}
            sisterInitials={sister.initials}
            sisterRing={sister.ringColor}
            isBot={!!sister.isBot}
          />
        ))}
        {lydiaTyping && (
          <div className="flex items-end gap-2">
            <div style={{ width: 32, height: 32, borderRadius: 999, background: "rgba(127,119,221,0.65)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ color: "#fff", fontSize: 13, fontWeight: 700 }}>SL</span>
            </div>
            <div style={{ background: "#fff", borderRadius: "18px 18px 18px 4px", padding: "10px 16px", border: "1px solid rgba(30,74,53,0.06)" }}>
              <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[0, 1, 2].map((i) => (
                  <span key={i} style={{ width: 6, height: 6, borderRadius: 999, background: "var(--mnf-tg)", display: "inline-block", animation: `bounce 1.2s ${i * 0.2}s infinite` }} />
                ))}
              </span>
            </div>
          </div>
        )}
        {lydiaError && (
          <div className="flex justify-center" role="alert" data-testid="banner-lydia-error">
            <div
              className="mnf-body text-center"
              style={{
                fontSize: 12,
                color: "var(--mnf-t)",
                padding: "10px 14px",
                background: "rgba(192,120,74,0.10)",
                border: "1px solid rgba(192,120,74,0.30)",
                borderRadius: 16,
                maxWidth: 300,
                lineHeight: 1.45,
              }}
            >
              <p style={{ marginBottom: 8 }}>{lydiaError}</p>
              <button
                onClick={retryLydia}
                data-testid="button-retry-lydia"
                className="mnf-body"
                style={{
                  fontSize: 12, fontWeight: 700,
                  padding: "6px 16px", borderRadius: 999,
                  background: "var(--mnf-t)", color: "#fff",
                }}
              >
                Ask again
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        className="px-3 pt-2 pb-4 flex items-end gap-2"
        style={{
          background: "rgba(245,240,232,0.95)",
          backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(30,74,53,0.06)",
        }}
      >
        {recording ? (
          <div className="flex-1 flex items-center gap-3 px-4 py-3" style={{ background: "rgba(192,120,74,0.10)", borderRadius: 22, border: "1px solid var(--mnf-t)" }}>
            <span style={{ width: 10, height: 10, borderRadius: 999, background: "var(--mnf-t)" }} className="animate-pulse" />
            <span className="mnf-body" style={{ fontSize: 13, fontWeight: 700, color: "var(--mnf-t)" }}>Recording…</span>
            <span className="mnf-body ml-auto" style={{ fontSize: 12, color: "var(--mnf-tg)", fontVariantNumeric: "tabular-nums" }}>0:08</span>
            <button
              onClick={() => setRecording(false)}
              data-testid="button-cancel-record"
              className="flex items-center justify-center"
              style={{ width: 32, height: 32, borderRadius: 999, background: "rgba(30,74,53,0.06)", color: "var(--mnf-tg)" }}
            >
              <Icon name="close" size={16} />
            </button>
            <button
              onClick={sendVoice}
              data-testid="button-send-voice"
              className="flex items-center justify-center"
              style={{ width: 38, height: 38, borderRadius: 999, background: "var(--mnf-t)", color: "#fff" }}
            >
              <Icon name="send" size={18} />
            </button>
          </div>
        ) : (
          <>
            <button
              data-testid="button-attach-verse"
              className="flex items-center justify-center shrink-0"
              style={{ width: 40, height: 40, borderRadius: 999, background: "rgba(127,119,221,0.12)", color: "var(--mnf-purple)" }}
            >
              <Icon name="menu_book" size={18} />
            </button>
            <div
              className="flex-1 flex items-center"
              style={{
                minHeight: 40,
                background: "#fff",
                border: "1px solid rgba(30,74,53,0.10)",
                borderRadius: 22,
                padding: "0 14px",
              }}
            >
              <input
                data-testid="input-message"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder={sister.isBot ? "Ask Sister Lydia…" : "A word for her…"}
                className="flex-1 bg-transparent outline-none mnf-body"
                style={{ fontSize: 14, color: "var(--mnf-fd)", padding: "10px 0" }}
              />
            </div>
            {draft.trim() ? (
              <button
                data-testid="button-send"
                onClick={send}
                className="flex items-center justify-center shrink-0"
                style={{ width: 40, height: 40, borderRadius: 999, background: "var(--mnf-f)", color: "#F5F0E8" }}
              >
                <Icon name="send" size={18} />
              </button>
            ) : (
              <button
                data-testid="button-record-voice"
                onClick={() => setRecording(true)}
                className="flex items-center justify-center shrink-0"
                style={{ width: 40, height: 40, borderRadius: 999, background: "var(--mnf-t)", color: "#fff" }}
              >
                <Icon name="mic" size={18} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- bubble -------------------------------- */
function Bubble({
  msg,
  sisterInitials,
  sisterRing,
  isBot,
}: {
  msg: ChatMessage;
  sisterInitials: string;
  sisterRing: string;
  isBot: boolean;
}) {
  // In Lydia threads: fromCoach=true means Lydia (left side, with avatar).
  // In sister-to-Kiley threads viewed by the sister: fromCoach=true means Kiley (left).
  // For all bot/coach replies, render on the left with avatar.
  const isFromAssistant = msg.fromCoach;
  const showAvatar = isFromAssistant; // assistant/coach side gets avatar
  const onRight = !isFromAssistant; // user side on right
  const align = onRight ? "items-end" : "items-start";
  const bubbleBg = onRight ? "var(--mnf-f)" : "#fff";
  const bubbleColor = onRight ? "#F5F0E8" : "var(--mnf-fd)";
  const bubbleBorder = onRight ? "none" : "1px solid rgba(30,74,53,0.06)";
  const bubbleRadius = onRight ? "20px 20px 6px 20px" : "20px 20px 20px 6px";

  return (
    <div className={`flex flex-col ${align}`}>
      <div className={`flex items-end gap-2 max-w-[85%] ${onRight ? "flex-row-reverse" : ""}`}>
        {showAvatar && <Avatar initials={sisterInitials} size={28} ringColor={sisterRing} />}
        <div>
          {msg.type === "text" && (
            <div
              className="mnf-body"
              style={{
                fontSize: 13.5, lineHeight: 1.5,
                padding: "10px 14px",
                borderRadius: bubbleRadius,
                background: isBot && isFromAssistant
                  ? "linear-gradient(135deg, rgba(127,119,221,0.10) 0%, rgba(168,212,184,0.10) 100%)"
                  : bubbleBg,
                color: isBot && isFromAssistant ? "var(--mnf-fd)" : bubbleColor,
                border: isBot && isFromAssistant ? "1px solid rgba(127,119,221,0.20)" : bubbleBorder,
                whiteSpace: "pre-wrap",
              }}
            >
              {msg.body}
            </div>
          )}
          {msg.type === "voice" && (
            <div
              className="flex items-center gap-3"
              style={{
                padding: "10px 14px",
                borderRadius: bubbleRadius,
                background: bubbleBg,
                color: bubbleColor,
                border: bubbleBorder,
                minWidth: 180,
              }}
            >
              <button
                className="flex items-center justify-center shrink-0"
                style={{
                  width: 32, height: 32, borderRadius: 999,
                  background: onRight ? "rgba(245,240,232,0.18)" : "var(--mnf-t)",
                  color: onRight ? "#F5F0E8" : "#fff",
                }}
                aria-label="Play voice note"
              >
                <Icon name="play_arrow" size={16} weight={600} />
              </button>
              <div className="flex items-center gap-0.5 flex-1">
                {[3, 6, 9, 12, 7, 4, 8, 11, 6, 9, 5, 8, 3].map((h, i) => (
                  <span key={i} style={{
                    width: 2.5, height: h, background: onRight ? "rgba(245,240,232,0.85)" : "var(--mnf-f)",
                    borderRadius: 999, opacity: 0.85,
                  }} />
                ))}
              </div>
              <span className="mnf-body shrink-0" style={{ fontSize: 11, fontVariantNumeric: "tabular-nums", opacity: 0.85 }}>
                0:{String(msg.durationSec || 0).padStart(2, "0")}
              </span>
            </div>
          )}
          {msg.type === "verse" && (
            <div
              className="relative overflow-hidden"
              style={{
                padding: "12px 14px",
                borderRadius: bubbleRadius,
                background: "linear-gradient(135deg, rgba(127,119,221,0.10) 0%, rgba(212,160,78,0.10) 100%)",
                border: "1px solid rgba(127,119,221,0.20)",
                color: "var(--mnf-fd)",
                maxWidth: 280,
              }}
            >
              <p className="mnf-eyebrow mb-1" style={{ color: "var(--mnf-purple)" }}>Scripture · shared</p>
              <p className="mnf-serif italic" style={{ fontSize: 14, lineHeight: 1.4, color: "var(--mnf-fd)" }}>
                {msg.verseRef ? `${msg.verseRef} · ` : ""}{msg.body}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className={`flex items-center gap-1 mt-1 ${onRight ? "pr-1" : "pl-9"}`}>
        <span className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)" }}>{formatTime(msg.sentAt)}</span>
      </div>
    </div>
  );
}
